import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchDescription } from '@/lib/branch'

import { ANY } from './constants'
import { useReportsBranchReach, useReportsLoanOfficers } from './queries'

/**
 * The branch narrowing every report in this module offers. One request behind it, shared with the
 * ledger and journal screens via `myBranchesQueryOptions`'s cache entry.
 */
export function BranchFilterField({
  id,
  label = 'Branch',
  branchId,
  onChange,
}: {
  id: string
  label?: string
  branchId: string | null
  onChange: (branchId: string | null) => void
}) {
  const reach = useReportsBranchReach()

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={branchId ?? ANY} onValueChange={(next) => onChange(next === ANY ? null : next)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Every branch you can reach</SelectItem>
          {reach.branches.map((branch) => (
            <SelectItem key={branch.id} value={branch.id}>
              {branchDescription(branch)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * The officer narrowing. Offered only once a branch is chosen and the directory answers with at
 * least one officer — a select with nothing in it would say "no officers exist" rather than the
 * true "pick a branch first", the same reasoning `GroupFilters` gives for the group module's own
 * officer select.
 */
export function LoanOfficerFilterField({
  id,
  label = 'Loan officer',
  branchId,
  loanOfficerId,
  onChange,
}: {
  id: string
  label?: string
  branchId: string | null
  loanOfficerId: string | null
  onChange: (loanOfficerId: string | null) => void
}) {
  const officers = useReportsLoanOfficers(branchId)

  if (!officers.data || officers.data.length === 0) {
    return null
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={loanOfficerId ?? ANY}
        onValueChange={(next) => onChange(next === ANY ? null : next)}
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any officer</SelectItem>
          {officers.data.map((staff) => (
            <SelectItem key={staff.userId} value={staff.userId}>
              {staff.fullName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
